import * as Sentry from '@sentry/react';
import { env } from 'constants/env';
import React, { useEffect } from 'react';
import { Route, Switch, useHistory } from 'react-router-dom';
import { GoogleAnalytics } from 'V2/Analytics';
import { ApiProvider } from 'V2/Api';
import { App } from 'V2/App';
import { ApplyProvider } from 'V2/Apply';
import { CheckoutVerification } from 'V2/Checkout/CheckoutVerification';
import { CheckoutPayment } from 'V2/Checkout/CheckoutPayment';
import { CheckoutConfirmation } from 'V2/Checkout/CheckoutConfirmation';
import { DocumentRender } from 'V2/DocumentRender';
import { EditProvider } from 'V2/Edit';
import { Search } from 'V2/Search';
import { postPageView } from 'V2/Services/PostPageView';
import { ErrorScreen } from 'V2/Shared/ErrorScreen';
import { IEBanner } from 'V2/Shared/IEBanner';
import { ThemeProvider } from 'V2/Shared/ThemeProvider';
import { PaymentProvider } from 'V2/Steps/PaymentDetails';
import { LogToService } from 'V2/Utilities/Logger';
import { MonocleProvider } from '@spur.us/monocle-react';
import { DesignVersioningProvider } from 'V2/Shared/DesignVersioning';
import { StateFlagsInitializer } from 'V2/Shared/StateFlagsInitializer';
import MonocleLoader from '@spur.us/monocle-loader';

export const Routes: React.FC = () => {
  const history = useHistory();
  const m = MonocleLoader({
    token: `${env.spurMonocleToken}`,
  });
  useEffect(() => {
    /*
      since this project is a SPA, we need to send every route change
      to analytics vendors that dont support client-side route changes
    */
    // @ts-ignore
    history.listen((historyLocation) => {
      GoogleAnalytics.send('pageview', historyLocation.pathname);

      // We post insurance app page views to Reznor.
      // These are forwarded to IdentityAPI, identified,
      // then published onto a topic as an event.
      // that event is consumed by Notification Worker
      // Notification worker then forwards that event to external services.
      // currently, this is CustomerIO.
      const appId = sessionStorage.getItem('appId');
      if (!appId) return;
      postPageView({
        appId,
        url: `${historyLocation.pathname}`,
        data: historyLocation,
      }).catch((error) => {
        if (error.response && error.response.data.http_error_code >= 500) {
          LogToService.captureException({
            errorType: 'server',
            message: error.response.data.message,
            stack: error.response.data.parent,
            thread: 'reznorPageViewApi',
            error: new Error(error),
          });
        }
      });
    });
  }, [history]);

  return (
    <Sentry.ErrorBoundary
      fallback={<ErrorScreen />}
      onError={(error: any) => {
        LogToService.event('error', {
          error_type: 'client',
          thread: 'ErrorBoundary',
          message: error.message,
        });
      }}
    >
      <MonocleProvider instance={m}>
        <ThemeProvider>
          <ApiProvider>
            <ApplyProvider>
              {({ policyState }) => (
                <EditProvider>
                  <PaymentProvider>
                    <StateFlagsInitializer state={policyState}>
                      <DesignVersioningProvider>
                        <Switch>
                          <Route exact path="/" component={App} />
                          <Route path="/apply" component={App} />
                          <Route path="/agent/apply" component={App} />
                          <Route path="/documents" component={DocumentRender} />
                          <Route path="/error" component={ErrorScreen} />
                        </Switch>
                      </DesignVersioningProvider>
                    </StateFlagsInitializer>
                  </PaymentProvider>
                </EditProvider>
              )}
            </ApplyProvider>
          </ApiProvider>
          <Route path="/search" component={Search} />
          <Switch>
            <Route exact path="/checkout/pay" component={CheckoutPayment} />
            <Route
              exact
              path="/checkout/confirmation"
              component={CheckoutConfirmation}
            />
            <Route path="/checkout/:token" component={CheckoutVerification} />
          </Switch>
          <IEBanner />
        </ThemeProvider>
      </MonocleProvider>
    </Sentry.ErrorBoundary>
  );
};
